import type { RefObject } from 'react';
import { Select } from '@/components/Select';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDataExport } from '@/state/experiments/useDataExport';
import { buildCurrencyOptions, buildGoalOptions } from './control-options';
import { Download } from 'lucide-react';

type SelectorsProps = {
  manager: ReturnType<typeof import('@/state/experiments/useExperimentStats').useExperimentStats>;
  chartData: Record<string, string | number>[];
  categories: string[];
  chartRef: RefObject<HTMLDivElement | null>;
};

// Evaluation method and multiple-comparison correction live with the
// significance table in EvaluationControls: they change how the results are
// judged, not what is being measured.
export const Selectors = ({ manager, chartData, categories, chartRef }: SelectorsProps) => {
  const { GOALS, goal, setGoal, goalLabels, currencies, currency, setCurrency } = manager;
  const { exportCSV, exportPNG } = useDataExport({ data: chartData, categories, chartRef });

  if (!currencies || currencies.length === 0) return null;

  const goalOptions = buildGoalOptions(GOALS, goalLabels);
  const currencyOptions = buildCurrencyOptions(currencies);

  return (
    <div className="flex flex-wrap items-end justify-between gap-4" data-tour="results-objective">
      <div className="max-w-2xl space-y-1">
        <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">Experiment results</h3>
        <p className="supporting">
          Sessions, conversions and revenue for each variant. The objective and currency chosen here apply to every
          table and chart below.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {/*
          SelectTrigger is w-full, so each select claims the whole row and they
          wrap onto separate lines. An explicit width overrides it via
          tailwind-merge and keeps them on one row.
        */}
        <label className="space-y-1">
          <span className="micro-label block">Objective</span>
          <Select
            options={goalOptions}
            value={goal}
            onValueChange={(value) => setGoal(value)}
            placeholder="Select goal"
            className="border-line bg-field h-9 w-44 rounded-lg"
          />
        </label>
        <label className="space-y-1">
          <span className="micro-label block">Currency</span>
          <Select
            options={currencyOptions}
            value={currency}
            onValueChange={(value) => setCurrency(value)}
            placeholder="Select currency"
            className="border-line bg-field h-9 w-28 rounded-lg"
          />
        </label>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="border-line bg-field text-foreground hover:bg-hover h-9 rounded-lg font-mono text-micro"
            >
              <Download className="mr-1.5 size-3.5" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Export to:</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={exportCSV}>CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={exportPNG}>PNG</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
